import { LitElement, css, html } from '@lit'
import { t } from '@nova/utils'
import * as paths from '@nova/paths'

class NovaSearchForm extends LitElement {
  static properties = {
    url: { type: String },
    value: { type: String },
    placeholder: { type: String },
    label: { type: String },
    noResults: { type: String },
    searchAgain: { type: String },
    openState: { type: String },
  }

  constructor() {
    super()
    this.searchData = {}
    this.searchTerm = ''
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('nova:header-drawer:open', this.handleAutoFocus.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('nova:header-drawer:open', this.handleAutoFocus.bind(this))
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties)
    this.searchInput = this.shadowRoot.querySelector('.header-search__input')
    this.searchInput.addEventListener('input', this.searchOnChange.bind(this))
    if (this.value) {
      this.getSearchResults(this.value)
    }
  }

  handleAutoFocus(event) {
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.searchInput.focus()
    }, 300)
  }

  searchOnChange(event) {
    if (this.openState === 'closed') return

    const searchValue = event.target.value
    this.value = searchValue
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.getSearchResults(searchValue)
    }, 300)
  }

  handleClearTerm(event) {
    event.preventDefault();
    this.searchInput.value = ''
    this.value = ''
    this.searchData = {}
    this.searchTerm = ''

    this.requestUpdate()
    clearTimeout(this.debounceTimeout)
    this.debounceTimeout = setTimeout(() => {
      this.searchInput.focus()
    }, 300)
    return
  }

  handleCloseDrawer(event) {
    event.preventDefault()
    this.parentElement.hide()
  }

  async getSearchResults(searchTerm) {
    if (!searchTerm.length) {
      this.searchData = {}
      this.searchTerm = ''

      this.requestUpdate()
      return
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(
          searchTerm,
        )}&resources[type]=product,collection&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,variants.barcode`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        this.searchData = {}
        this.searchTerm = ''
      }
      const data = await response.json()
      this.searchData = data
      this.searchTerm = searchTerm
    } catch (error) {
      console.error('Error:', error.message)
      this.searchData = {}
      this.searchTerm = ''
    }

    this.requestUpdate()
  }

  render() {
    return html`
      <form
        class="header-search__form"
        action=${this.url}
        method="get"
        role="search"
      >
        <div class="field header-search__field">
          <sl-input
            class="header-search__input"
            id="Search-In-Modal"
            type="search"
            name="q"
            value=${this.value}
            placeholder=""
            label="Search"
            help-text=${window.langMessages['general.search.help_text']}
          >
            <span slot="suffix" class="header-search__input-suffix">
              <button
                class="search-clear-button"
                @click=${this.handleClearTerm}
              >
                ${window.langMessages['general.search.clear']}
              </button>
              <sl-icon-button
                name="x-lg"
                class="search-close-button"
                label=${window.langMessages['accessibility.close']}
                @click=${this.handleCloseDrawer}>
              </sl-icon-button>
            </span>
          </sl-input>
          <input type="hidden" name="options[prefix]" value="last" />
          <input type="hidden" name="type" value="product" />
          <button
            class="search-close-button-mobile"
            alt=${window.langMessages['accessibility.close']}
            @click=${this.handleCloseDrawer}
          >
            ${paths.closeSmall}
          </button>
        </div>
      </form>
      <nova-search-result
        .searchData=${this.searchData}
        .searchTerm=${this.searchTerm}
        openState=${this.openState}
        noResults=${t('general.search.no_results_found_for')}
        searchAgain=${t('general.search.try_again')}
      ></nova-search-result>
    `
  }

  static styles = css`
    :host {
      width: 100%;
      overflow: auto;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }

    :host::-webkit-scrollbar {
      display: none;
      width: 0;
    }

    :host .header-search__form {
      width: 100%;
    }

    :host .header-search__field {
      display: flex;
      align-items: center;
      position: relative;
    }

    :host .header-search__input {
      width: 100%;
      border: none;
    }

    :host .visually-hidden {
      position: absolute !important;
      overflow: hidden;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      word-wrap: normal !important;
    }

    :host .header-search__input:focus-visible {
      outline: none;
    }

    .header-search__input::part(form-control-label) {
      display: none;
    }

    .header-search__input::part(form-control-help-text) {
      font: var(--nova-body-caption);
      line-height: calc(10 / 12 * 100%);
      color: var(--nova-color-text);
      margin-top: var(--nova-space-sm);
    }

    @media screen and (min-width: 768px) {
      .header-search__input::part(form-control-help-text) {
        margin-top: var(--nova-space-xsm);
      }
    }

    .header-search__input::part(base) {
      background-color: transparent;
      border-radius: 0;
      border: 0;
      border-bottom: solid 1px var(--nova-color-brand-taupe);
      min-height: 50px;
      box-shadow: none;
    }

    .header-search__input::part(input) {
      padding: var(--nova-space-sm) 0;
      height: auto;
      font: var(--nova-h6-font);
      font-weight: 700;
      font-family: var(--nova-font-sans-bold);
      color: var(--nova-color-text);
      text-transform: uppercase;
    }

    /* Clear default clear button from Internet Explorer */
    :host input[type='search']::-ms-clear {
      display: none;
      -webkit-appearance: none;
      width: 0;
      height: 0;
    }

    /* Clear default clear button from Chrome */
    :host input[type='search']::-webkit-search-cancel-button {
      display: none;
      -webkit-appearance: none;
    }

    ::slotted([slot='search-icon']) {
      padding-right: var(--nova-space-xs);
    }

    @media screen and (min-width: 768px) {
      ::slotted([slot='search-icon']) {
        display: none;
      }
    }

    .search-clear-button {
      color: var(--nova-color-text);
      background: transparent;
      border: 0;
      border-bottom: solid 1px var(--nova-color-text);
      padding: 0 0 var(--nova-space-2xs) 0;
      font: var(--nova-h8-font);
      text-transform: uppercase;
      cursor: pointer;
    }

    .header-search__input-suffix {
      margin-inline-end: 0;
      gap: var(--nova-space-lg);
      display: flex;
      align-items: center;
    }

    .search-close-button {
      display: none;
    }

    .search-close-button-mobile {
      position: absolute;
      top: -22px;
      right: -17px;
      display: flex;
      padding: 7px;
      border: 0;
      background: transparent;
      border-radius: 0;
    }

    .search-close-button-mobile svg {
      width: 10px;
      height: 10px;
    }

    @media screen and (min-width: 768px) {
      .search-clear-button {
        font: var(--nova-h7-font);
      }

      .search-close-button-mobile {
        display: none;
      }

      .search-close-button {
        display: flex;
        color: var(--nova-color-text);
      }

      .search-close-button::part(base) {
        padding: 0;
        font-size: 24px;
      }

      .search-close-button::part(base):hover {
        color: var(--nova-color-text);
      }
    }
  `
}

customElements.define('nova-search-form', NovaSearchForm)
